'use client';
import styled from '@benzinga/themetron';
import React, { useState } from 'react';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { Author } from '@benzinga/article-manager';
import { AuthorSocialMedia, AuthorSocialMediaLinksI } from './AuthorSocialMedia';
import { truncate } from '@benzinga/utils';
import { FollowAuthorButton } from '@benzinga/core-ui';

interface AuthorInfoProps {
  author: Author;
}

export const AuthorInfo: React.FC<AuthorInfoProps> = ({ author }) => {
  const [isShowingMore, setIsShowingMore] = useState(false);

  const handleShowMore = () => setIsShowingMore(!isShowingMore);

  const social: AuthorSocialMediaLinksI = {
    facebook: author.facebook,
    linkedin: author.linkedin,
    twitter: author.twitter,
    uid: author.uid,
  };

  return (
    <AuthorInfoWrapper className="author-info">
      <div className="grid grid-cols-1 gap-5">
        <div className="flex sm:items-center gap-3">
          <div className="avatar">
            <img alt={author.name} className="w-full h-full object-cover object-top rounded" src={author.picture} />
          </div>
          <div className="author-content">
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <div>
                <h1 className="text-3xl">{author.name}</h1>
                <p className="mb-0 text">{author.byLine}</p>
                <p className="mb-2 text font-xs">{author.byLine_secondary}</p>
                <div className="flex gap-3">
                  <FollowAuthorButton authorName={author.name} authorUid={author.uid} className="large" />
                  <AuthorSocialMedia social={social} />
                </div>
              </div>
            </div>
          </div>
        </div>
        {author.about && (
          <div>
            <h5 className="heading">About</h5>
            <div className="flex flex-col gap-2 mb-4">
              <div
                className="text-sm"
                dangerouslySetInnerHTML={{
                  __html: isShowingMore ? author.about : sanitizeHTML(truncate(author.about, 250)),
                }}
              />
              {author.about.length > 250 && (
                <button className="text-blue-400 text-sm inline-flex" onClick={() => handleShowMore()}>{`Show ${
                  isShowingMore ? 'Less' : 'More'
                }`}</button>
              )}
            </div>
          </div>
        )}
      </div>
    </AuthorInfoWrapper>
  );
};

export default AuthorInfo;

const AuthorInfoWrapper = styled.div`
  .avatar {
    border-radius: 5px;
    width: 115px;
    height: 115px;
    overflow: hidden;
  }
  .author-content {
    width: calc(100% - 115px);
  }
  .text {
    font-size: 14px;
    color: #c0c0c0;
  }
  .gap-1 {
    gap: 4px;
  }
  .gap-3 {
    gap: 12px;
  }
  .gap-4 {
    gap: 16px;
  }
  .gap-5 {
    gap: 20px;
  }
`;
